/**
 * Tenant-aware feature visibility
 *
 * Combines runtime tenant context (type + role) with static deployment flags
 * to determine which UI sections should be shown.
 *
 * Usage:
 *   const { showGroupManagement, showUserManagement } = useTenantFeatures();
 */

import { useTenantType, useIsAdmin, useIsMultiUser } from 'src/stores/tenant.store';

export interface TenantFeatures {
  /** Show the group management page (admin only, multi-user tenants) */
  showGroupManagement: boolean;
  /** Show the user/member management page (admin only, multi-user tenants) */
  showUserManagement: boolean;
  /** Show role badges next to user names */
  showRoleBadge: boolean;
  /** Show admin-only settings panel */
  showAdminPanel: boolean;
  /** Show group membership info on user profiles */
  showGroupMembership: boolean;
  /** Whether the tenant has more than one user (or could) */
  isMultiUser: boolean;
  /** Whether the current user is an admin */
  isAdmin: boolean;
}

export function useTenantFeatures(): TenantFeatures {
  const tenantType = useTenantType();
  const isAdmin = useIsAdmin();
  const isMultiUser = useIsMultiUser();

  return {
    showGroupManagement: isMultiUser && isAdmin,
    showUserManagement: isMultiUser && isAdmin,
    showRoleBadge: isMultiUser,
    showAdminPanel: isAdmin,
    showGroupMembership: isMultiUser,
    isMultiUser,
    isAdmin,
  };
}
